import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import {
  useAddCrmSectionInstanceMutation,
  useCreateCrmLayoutNodeMutation,
  useUpdateCrmLayoutNodeMutation,
} from "store/apis/crmFields";
import { CrmLayoutNodeInput } from "types/Models/crmFields";

type NodeKind = "tab" | "section";

/** Shared add/rename dialog for the two title-only layout nodes - a tab and a section. */
export const CRMNodeTitleModal = ({
  isOpen,
  close,
  entityId,
  nodeKind,
  nodeId,
  parentId,
  initialTitle = "",
}: {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  nodeKind: NodeKind;
  nodeId?: string | null;
  parentId?: string | null;
  initialTitle?: string;
}) => {
  const { alertToast, successToast } = useToast();
  const [createNode, { isLoading: isCreating }] =
    useCreateCrmLayoutNodeMutation();
  const [updateNode, { isLoading: isUpdating }] =
    useUpdateCrmLayoutNodeMutation();
  const [addInstance] = useAddCrmSectionInstanceMutation();
  const isEdit = Boolean(nodeId);

  const isSection = nodeKind === "section";

  const onSubmit = async ({
    title,
    is_repeatable,
  }: {
    title: string;
    is_repeatable: boolean;
  }) => {
    const body = {
      [nodeKind]: isEdit
        ? { node_id: nodeId, title }
        : {
            parent_id: parentId || undefined,
            title,
            ...(isSection ? { is_repeatable } : {}),
          },
    } as CrmLayoutNodeInput;

    try {
      const mutation = isEdit ? updateNode(body) : createNode(body);
      const result = await mutation.unwrap();
      successToast({ message: isEdit ? "Renamed" : `${nodeKind} added` });
      close();

      if (!isEdit && isSection && is_repeatable) {
        const sectionId = (result as { uuid: string }).uuid;
        try {
          await addInstance({ entityId, sectionId }).unwrap();
        } catch (error: any) {
          alertToast({
            message:
              error?.data?.message ||
              "Section created, but couldn't add its first entry",
          });
        }
      }
    } catch (error: any) {
      alertToast({ message: error?.data?.message || "Could not save" });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay>
        <Formik
          initialValues={{ title: initialTitle, is_repeatable: false }}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values, submitForm, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {isEdit ? `Rename ${nodeKind}` : `Add ${nodeKind}`}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form className="t-m-0 t-flex t-flex-col t-gap-3">
                  <TextInput name="title" label="Title" required />
                  {isSection && !isEdit && (
                    <div className="t-flex t-items-center t-gap-2">
                      <Checkbox
                        name="is_repeatable"
                        checked={values.is_repeatable}
                        onChange={(event) =>
                          setFieldValue("is_repeatable", event.target.checked)
                        }
                      />
                      <span className="t-text-body-sm">
                        Repeatable (lets users add multiple entries, e.g. one
                        per bank account)
                      </span>
                    </div>
                  )}
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  isLoading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating || !values.title}
                >
                  {isEdit ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
